import React, {useCallback, useEffect, useMemo, useState} from "react";
import {db} from '../../../firebase/FirebaseProvider'
import {
    doc,
    collection,
    getDoc,
    getDocs,
    query,
    orderBy,
    where
} from 'firebase/firestore'
import {Location, Session, User} from "../../types/Types";
import {Button, Card, ListGroup} from "react-bootstrap";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import UserNav from "../UserNav";
import SessionRow from "../../shared/SessionRow";
import DateTime from "../../date/DateTime";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams} from 'react-router-dom';
import {addCountryVisited} from "../../util/UserUtil";
import {getCountryCode, getDateFromTimeStamp, getDistanceFromLatLonInKm, formatKilometres, formatTimeBetweenDetailed} from "../../util/Util";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import V2YearPicker from "../../shared/v2/V2YearPicker";
import VerificationTag from "../../tag/VerificationTag";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBolt} from "@fortawesome/free-solid-svg-icons";

interface Props {
    locations: Array<Location> | undefined,
    currentUser: User | undefined,
}

function getSessionYear(value: any): number {
    return new Date(getSessionTimestamp(value)).getFullYear();
}

function getSessionTimestamp(value: any): number {
    const date = value?.seconds
        ? new Date(value.seconds * 1000)
        : value?.toDate
            ? value.toDate()
            : new Date(value);
    return date.getTime();
}

function UserSessions(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [sessions, setSessions] = useState<Array<Session> | undefined>(undefined);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [sessionFilter, setSessionFilter] = useState<'ALL' | 'UNIQUE'>('ALL');
    const [year, setYear] = useState<string>('1');
    const isUiV2 = !!props.currentUser?.enableUiV2;

    useEffect(() => {
        if (!id) return;
        
        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                let userToSet = docSnap.data() as User;
                userToSet.id = docSnap.id;
                setUser(userToSet);
            }
        })();
    }, [id]);

    const updateCountriesVisited = useCallback(async (sessions: Array<Session>, user: User) => {
        console.log('updateCountriesVisited called for user:', user.id);
        
        if (!sessions || sessions.length === 0) {
            console.log('No sessions to process');
            return;
        }

        if (!props.locations) {
            console.log('No locations data available');
            return;
        }

        // Create a map of location IDs to locations for quick lookup
        const locationMap = new Map<string, Location>();
        props.locations.forEach(location => {
            locationMap.set(location.id, location);
        });

        // Get unique countries from sessions
        const uniqueCountries = new Set<string>();
        sessions.forEach(session => {
            const location = locationMap.get(session.locationId);
            if (location && location.country) {
                uniqueCountries.add(location.country);
            }
        });

        console.log(`Found ${uniqueCountries.size} unique countries from sessions:`, Array.from(uniqueCountries));

        // Convert country names to codes
        const uniqueCountryCodes = new Set<string>();
        uniqueCountries.forEach(countryName => {
            const countryCode = getCountryCode(countryName);
            if (countryCode) {
                uniqueCountryCodes.add(countryCode);
            }
        });

        console.log(`Converted to ${uniqueCountryCodes.size} country codes:`, Array.from(uniqueCountryCodes));

        // Get current user's countries visited
        const existingCountries = user.countriesVisited || [];
        console.log('Existing countries:', existingCountries);

        // Find countries that need to be added
        const countriesToAdd = Array.from(uniqueCountryCodes).filter(
            country => !existingCountries.includes(country)
        );

        if (countriesToAdd.length === 0) {
            return;
        }

        const successfullyAdded: string[] = [];
        for (const country of countriesToAdd) {
            try {
                const result = await addCountryVisited(country, user.id);
                if (result.code === 'SUCCESS') {
                    successfullyAdded.push(country);
                }
            } catch (error) {
                console.error(`Error adding country ${country}:`, error);
            }
        }

        if (successfullyAdded.length > 0 && user) {
            console.log(`Updated local user countriesVisited:`, user.countriesVisited);
        }
    }, [props.locations]);

    const loadAllData = useCallback(async() => {
        if(!user) {
            console.log('No user, skipping loadAllData');
            return;
        }
        
        let sessions: Array<Session> = [];
        const sessionOnceCollection = query(collection(db, 'sessions'),
            where('userId', '==', user.id),
            orderBy('checkInTime', 'desc'));
        const sessionOnceCollectionSnapshot = await getDocs(sessionOnceCollection);
        sessionOnceCollectionSnapshot.forEach((doc) => {
            let session = doc.data() as Session;
            sessions.push(session);
        });
        console.log(`Loaded user sessions: ${sessions.length} documents`);
        setSessions(sessions);

        await updateCountriesVisited(sessions, user);
    }, [user, updateCountriesVisited]);

    useEffect(() => {
        if (!user) return;
        loadAllData();
    }, [user, loadAllData]);

    const locationMap = useMemo(() => {
        const map = new Map<string, Location>();
        (props.locations || []).forEach(location => map.set(location.id, location));
        return map;
    }, [props.locations]);

    const sessionsForYear = useMemo(() => {
        if (!sessions || year === '1') return sessions;
        return sessions.filter((session) => getSessionYear(session.checkInTime) === Number(year));
    }, [sessions, year]);

    const availableSessionYears = useMemo(() => {
        if (!sessions) return [];
        return Array.from(new Set(sessions.map((session) => getSessionYear(session.checkInTime))));
    }, [sessions]);

    const firstSessionTimestampByLocation = useMemo(() => {
        const firstSessions = new Map<string, number>();
        (sessions || []).forEach((session) => {
            const timestamp = getSessionTimestamp(session.checkInTime);
            const firstTimestamp = firstSessions.get(session.locationId);
            if (firstTimestamp === undefined || timestamp < firstTimestamp) {
                firstSessions.set(session.locationId, timestamp);
            }
        });
        return firstSessions;
    }, [sessions]);

    const uniqueSessionCount = useMemo(() => {
        if (!sessionsForYear) return 0;
        return new Set(
            sessionsForYear
                .filter((session) => firstSessionTimestampByLocation.get(session.locationId) === getSessionTimestamp(session.checkInTime))
                .map((session) => session.locationId)
        ).size;
    }, [sessionsForYear, firstSessionTimestampByLocation]);

    const displayedSessions = useMemo(() => {
        if (!sessionsForYear) return undefined;
        if (sessionFilter === 'ALL') return sessionsForYear;

        const uniqueLocationIds = new Set<string>();
        return sessionsForYear.filter((session) => {
            const isFirstCheckIn = firstSessionTimestampByLocation.get(session.locationId) === getSessionTimestamp(session.checkInTime);
            if (!isFirstCheckIn || uniqueLocationIds.has(session.locationId)) {
                return false;
            }
            uniqueLocationIds.add(session.locationId);
            return true;
        });
    }, [sessionsForYear, sessionFilter, firstSessionTimestampByLocation]);

    const travelledKm = useMemo(() => {
        if (!displayedSessions) return 0;

        let totalKm = 0;
        for (let i = 0; i < displayedSessions.length - 1; i++) {
            const location = locationMap.get(displayedSessions[i].locationId);
            const nextLocation = locationMap.get(displayedSessions[i + 1].locationId);
            if (location && nextLocation && location.lat && location.lng && nextLocation.lat && nextLocation.lng) {
                totalKm += getDistanceFromLatLonInKm(
                    Number(location.lat),
                    Number(location.lng),
                    Number(nextLocation.lat),
                    Number(nextLocation.lng)
                );
            }
        }
        return totalKm;
    }, [displayedSessions, locationMap]);

    const v2DetailHeader = (
        <div className="user-v2-subpage-header-content">
            <div className="user-v2-subpage-header-stats">
                <span>User has travelled <b>{formatKilometres(travelledKm)}</b></span>
            </div>
            <V2YearPicker value={year} onChange={setYear} availableYears={availableSessionYears} ariaLabel="Select check-in year" />
        </div>
    );

    return (
        <>
            <PageTracking />
            {isUiV2 ? <UserDetailsV2
                currentUser={props.currentUser}
                user={user}
                locations={props.locations}
                detailHeader={sessions && sessions.length > 0 ? v2DetailHeader : undefined}
                detailStatValues={{
                    "Check-ins": sessionsForYear?.length || 0,
                    "Unique": uniqueSessionCount,
                }}
                detailStatActions={{
                    "Check-ins": {active: sessionFilter === 'ALL', onClick: () => setSessionFilter('ALL')},
                    "Unique": {active: sessionFilter === 'UNIQUE', onClick: () => setSessionFilter('UNIQUE')},
                }}
            /> : <UserDetails user={user}/>}
            {!isUiV2 && <UserNav currentUser={props.currentUser} user={user}/>}
            {(() => {
                const ListContainer = isUiV2 ? V2GlassPanel : Card;
                return <ListContainer className={isUiV2 ? "user-v2-checkins-list-card v2-glass-list-panel" : "mb-3"}>
                {!isUiV2 && sessions && sessions.length > 0 && <Card.Header className={"header-padding"}>
                    <div className={"d-flex justify-content-between align-items-center"}>
                        <div>
                            <div className={"d-inline-block stats-card mr-2"}>
                                Total: <b>{sessionFilter === 'UNIQUE' ? user?.sessionVerifiedCount : sessions.length}</b>
                            </div>
                            {travelledKm > 0 && (
                                    <div className={"d-inline-block stats-card mr-2"}>
                                        <span>Travelled: <b>{formatKilometres(travelledKm)}</b></span>
                                    </div>
                            )}
                        </div>
                        <div>
                            <Button
                                variant="primary"
                                size="sm"
                                className="mr-2"
                                disabled={sessionFilter === 'ALL'}
                                onClick={() => setSessionFilter('ALL')}
                            >
                                All
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                disabled={sessionFilter === 'UNIQUE'}
                                onClick={() => setSessionFilter('UNIQUE')}
                            >
                                Unique
                            </Button>
                        </div>
                    </div>
                </Card.Header>}
                {isUiV2 ? (
                    <>
                        {displayedSessions?.map((session: Session, index: number) => {
                            let timeSince: string | undefined;
                            let distanceKm: string | undefined;
                            let speedKmh: number | undefined;

                            if (index < displayedSessions.length - 1) {
                                const prevSession = displayedSessions[index + 1];
                                if (prevSession) {
                                    timeSince = formatTimeBetweenDetailed(prevSession.checkInTime, session.checkInTime);
                                    const loc = locationMap.get(session.locationId);
                                    const prevLoc = locationMap.get(prevSession.locationId);
                                    if (loc && prevLoc && loc.lat && loc.lng && prevLoc.lat && prevLoc.lng) {
                                        const dist = getDistanceFromLatLonInKm(
                                            Number(loc.lat), Number(loc.lng),
                                            Number(prevLoc.lat), Number(prevLoc.lng)
                                        );
                                        distanceKm = formatKilometres(dist);
                                        const t1: any = session.checkInTime;
                                        const t2: any = prevSession.checkInTime;
                                        const ms1 = t1?.seconds ? t1.seconds * 1000 : new Date(t1).getTime();
                                        const ms2 = t2?.seconds ? t2.seconds * 1000 : new Date(t2).getTime();
                                        const hours = Math.abs(ms1 - ms2) / 3600000;
                                        if (hours > 0) speedKmh = dist / hours;
                                    }
                                }
                            }

                            const isCheckedIn = getDateFromTimeStamp(session.checkOutTime) > new Date();
                            const location = locationMap.get(session.locationId);

                            return (
                                <V2GlassSurface key={session.id || index} className="user-v2-checkin-row v2-glass-list-row">
                                    <div className="user-v2-checkin-main">
                                        <div className="user-v2-checkin-title-line">
                                            <Link className="user-v2-location-name-link" to={`/location/${session.locationId}/sessions`}>
                                                <V2GlassChip className="v2-glass-location-chip">{session.locationName}</V2GlassChip>
                                            </Link>
                                            <VerificationTag active={isCheckedIn}/>
                                        </div>
                                        {location?.address && <div className="user-v2-checkin-meta">{location.address}</div>}
                                    </div>
                                    <div className="user-v2-checkin-details">
                                        {(timeSince || distanceKm || (speedKmh !== undefined && speedKmh > 50)) && (
                                            <div className="user-v2-checkin-metrics">
                                                {timeSince && <span>{timeSince}</span>}
                                                {timeSince && distanceKm && <span>|</span>}
                                                {distanceKm && <span>{distanceKm}</span>}
                                                {speedKmh !== undefined && speedKmh > 50 && <span><FontAwesomeIcon icon={faBolt} /> {Math.round(speedKmh)} km/h</span>}
                                            </div>
                                        )}
                                        <V2GlassChip className="user-v2-checkin-date-chip">
                                            <DateTime date={session.checkInTime}/>
                                        </V2GlassChip>
                                    </div>
                                </V2GlassSurface>
                            );
                        })}
                        {!sessions && <div className={"loading"}>Loading...</div>}
                        {(displayedSessions && displayedSessions.length === 0) && <div className={'loading'}>No check-ins for this year</div>}
                    </>
                ) : (
                    <ListGroup variant="flush">
                        {displayedSessions?.map((session: Session, index: number) => {
                            let timeSince: string | undefined;
                            let distanceKm: string | undefined;
                            let speedKmh: number | undefined;

                            if (index < displayedSessions.length - 1) {
                                const prevSession = displayedSessions[index + 1];
                                timeSince = formatTimeBetweenDetailed(prevSession.checkInTime, session.checkInTime);
                                const loc = locationMap.get(session.locationId);
                                const prevLoc = locationMap.get(prevSession.locationId);
                                if (loc && prevLoc && loc.lat && loc.lng && prevLoc.lat && prevLoc.lng) {
                                    const dist = getDistanceFromLatLonInKm(Number(loc.lat), Number(loc.lng), Number(prevLoc.lat), Number(prevLoc.lng));
                                    distanceKm = formatKilometres(dist);
                                    const t1: any = session.checkInTime;
                                    const t2: any = prevSession.checkInTime;
                                    const ms1 = t1?.seconds ? t1.seconds * 1000 : new Date(t1).getTime();
                                    const ms2 = t2?.seconds ? t2.seconds * 1000 : new Date(t2).getTime();
                                    const hours = Math.abs(ms1 - ms2) / 3600000;
                                    if (hours > 0) speedKmh = dist / hours;
                                }
                            }

                            return <SessionRow key={session.id || index} displayBonus={false} displayName={false} displayLocation={true} session={session} user={user} timeSince={timeSince} distanceKm={distanceKm} speedKmh={speedKmh}/>;
                        })}
                        {!sessions && <div className={"loading"}>Loading...</div>}
                        {(displayedSessions && displayedSessions.length === 0) && <div className={'loading'}>No check-ins for this year</div>}
                    </ListGroup>
                )}
                </ListContainer>;
            })()}
        </>
    );
}

export default UserSessions;
